import { z } from 'zod'
import { Model } from './architecture'
import { ID } from './ID'

export const ListOfIDValidation = z.object({
  list: z.instanceof(ID, { message: '[ListOfID] list is required' }).array(),
})

export class ListOfID extends Model {
  static create({ list }: z.infer<typeof ListOfIDValidation>) {
    ListOfIDValidation.parse({ list })
    return new ListOfID(list)
  }

  static fromString({ ids }: { ids: string }) {
    return ListOfID.create({ list: ids.split(',').map(id => ID.create({ value: id })) })
  }

  static empty() {
    return new ListOfID([])
  }

  constructor(private readonly _list: ID[]) {
    super()
  }

  get list() { return this._list } // prettier-ignore
  get length() { return this._list.length } // prettier-ignore

  map<T>(fn: (id: ID) => T) {
    return this._list.map(fn)
  }

  at({ position }: { position: number }) {
    return this._list.at(position)
  }

  findOne({ id }: { id: ID }) {
    return this._list.find(item => item.value === id.value)
  }

  addOne({ id, limit }: { id: ID; limit?: number }) {
    if (this.findOne({ id })) {
      return this
    }

    const newList = [id, ...this._list]

    if (limit === undefined) {
      return ListOfID.create({ list: newList })
    }

    return ListOfID.create({ list: newList.slice(0, limit) })
  }

  removeOne({ id }: { id: ID }) {
    return ListOfID.create({ list: this._list.filter(item => item.value !== id.value) })
  }

  toString() {
    return this._list.map(item => item.value).join(',')
  }

  toJSON() {
    return { list: this._list.map(item => item.toJSON()) }
  }

  isEmpty() {
    return this._list.length === 0
  }
}
